import React, { useCallback, useId, useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import { toast } from 'react-hot-toast';
import ClockLoader from './ClockLoader';
import { uploadTenantRegistryLogo } from 'shared/api/tenantLogoUpload';
import { absoluteAssetUrlForStoredPath } from 'shared/api/services/publicTenantBrandingService';

/**
 * Picks an image from disk, uploads via PUT /api/v1/files/tenant-logo, writes API path into Formik (tenantPhoto).
 */
const TenantLogoUploadField = ({
  value,
  setFieldValue,
  registryTenantId,
  disabled = false,
  error,
  helperText = 'PNG or JPEG, max 5MB. Path is stored when you save the tenant.',
  /** When true, only pick a file locally; parent uploads after tenant row exists (create flow). */
  deferredUpload = false,
  pendingFile,
  onPendingFileChange,
}) => {
  const inputId = useId();
  const [uploading, setUploading] = useState(false);
  const [localPreviewUrl, setLocalPreviewUrl] = useState('');

  React.useEffect(() => {
    if (!deferredUpload || !pendingFile) {
      setLocalPreviewUrl('');
      return undefined;
    }
    const url = URL.createObjectURL(pendingFile);
    setLocalPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [deferredUpload, pendingFile]);

  const handleFile = useCallback(
    async (e) => {
      const file = e.target.files?.[0];
      e.target.value = '';
      if (!file) return;

      if (!file.type.startsWith('image/')) {
        toast.error('Please select a valid image file');
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        toast.error('File size must be less than 5MB');
        return;
      }

      if (deferredUpload) {
        onPendingFileChange?.(file);
        setFieldValue('tenantPhoto', '');
        toast.success('Logo selected — it will upload when you create the tenant');
        return;
      }

      setUploading(true);
      try {
        const path = await uploadTenantRegistryLogo(file, registryTenantId);
        if (!path) {
          throw new Error('Upload did not return a path');
        }
        setFieldValue('tenantPhoto', path);
        onPendingFileChange?.(null);
        toast.success('Logo uploaded');
      } catch (err) {
        const status = err.response?.status;
        let apiMessage = err.response?.data?.message || err.response?.data?.error;
        if (!apiMessage && typeof err.response?.data === 'string') {
          try {
            apiMessage = JSON.parse(err.response.data)?.message;
          } catch {
            apiMessage = err.response.data;
          }
        }
        const msg =
          apiMessage ||
          (status ? `Upload failed (${status})` : null) ||
          err.message ||
          'Failed to upload logo';
        toast.error(typeof msg === 'string' ? msg : 'Failed to upload logo');
      } finally {
        setUploading(false);
      }
    },
    [deferredUpload, onPendingFileChange, registryTenantId, setFieldValue]
  );

  const previewSrc = deferredUpload && localPreviewUrl
    ? localPreviewUrl
    : value
      ? absoluteAssetUrlForStoredPath(value)
      : '';
  const hasPreview = Boolean(previewSrc);

  return (
    <Box
      sx={{
        gridColumn: { xs: 'auto', sm: '1 / -1' },
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
      }}
    >
      <Typography variant="caption" color="text.secondary">
        Tenant logo
      </Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.5 }}>
        <input
          id={inputId}
          type="file"
          accept="image/*"
          hidden
          disabled={disabled || uploading}
          onChange={handleFile}
        />
        <label htmlFor={inputId} style={{ margin: 0 }}>
          <Button
            variant="outlined"
            component="span"
            disabled={disabled || uploading}
            size="small"
          >
            {uploading ? (
              <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
                <ClockLoader inline size={18} />
                Uploading…
              </Box>
            ) : (
              'Choose image from computer'
            )}
          </Button>
        </label>
        {hasPreview ? (
          <>
            <Button
              type="button"
              size="small"
              color="inherit"
              disabled={disabled || uploading}
              onClick={() => {
                if (deferredUpload) {
                  onPendingFileChange?.(null);
                }
                setFieldValue('tenantPhoto', '');
              }}
            >
              Remove logo
            </Button>
            <Box
              component="img"
              src={previewSrc}
              alt="Tenant logo preview"
              sx={{
                maxHeight: 56,
                maxWidth: 160,
                objectFit: 'contain',
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'divider',
              }}
            />
          </>
        ) : null}
      </Box>
      {value && !deferredUpload ? (
        <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-all' }}>
          {value}
        </Typography>
      ) : null}
      {error ? (
        <Typography variant="caption" color="error" display="block">
          {error}
        </Typography>
      ) : null}
      <Typography variant="caption" color="text.secondary">
        {helperText}
      </Typography>
    </Box>
  );
};

export default TenantLogoUploadField;
